
import React, { useState, useEffect } from "react";
import { DataToolbar, ActionButtons } from "../components/DataToolbar";
import { PreviewModal } from "../components/PreviewModal";
import { Modal } from "../components/Modal";
import { exportToExcel } from "../export/excel";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useAiContext } from "../ai/AiContext"; 
import { useToast } from "../components/Toast";
import { db, serverTimestamp } from "../../firebaseClient";
import { uploadTaskFiles } from "../services/storageUpload";
import { createLegalWorkLog, updateLegalWorkLog } from "../services/legalWorkLogService";
import type { AttachmentMeta } from "../types/attachments";
import { useAuth } from "../context/AuthContext";
import type { LegalWorkLog } from "../types/legalWorkLog";
import { DateField } from "../components/form/DateField";
import { formatDate, parseDate } from "../utils/dateUtils";
import { canEditModule, getDeptCodeByEmail, isGovAdmin } from "../config/roles";
import { DEPT_CODE_TO_NAME } from "../constants/departments";
import { SimpleApprovalSection, INITIAL_APPROVAL_STEPS } from "../components/shared/SimpleApprovalSection";

export function TasksView() {
  const { user, debugViewDepartmentId } = useAuth();
  const canEdit = user ? canEditModule(user.role, 'TASKS_AND_LOG') : false;
  const userDeptCode = getDeptCodeByEmail(user?.email);
  const isDeptUser = user?.role === 'dept-minimal';

  const [items, setItems] = React.useState<LegalWorkLog[]>([]);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState<LegalWorkLog | null>(null);
  const [previewItem, setPreviewItem] = React.useState<LegalWorkLog | null>(null);
  
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [exportStart, setExportStart] = React.useState("");
  const [exportEnd, setExportEnd] = React.useState("");
  const [error, setError] = React.useState<Error | null>(null);

  const { requestDelete } = useAiContext();
  const { showToast } = useToast();

  React.useEffect(() => {
    let q = db.collection("legal_work_logs").orderBy("createdAt", "desc");
    
    // Effective filtering: Either Debug Mode ID OR Real User Dept Code
    const effectiveDeptCode = debugViewDepartmentId || (isDeptUser ? userDeptCode : null);

    if (effectiveDeptCode) {
        q = q.where("assignedDeptCodes", "array-contains", effectiveDeptCode);
    }

    const unsubscribe = q.onSnapshot((snapshot) => {
      const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as LegalWorkLog));
      const pending = all.filter(t => 
        t.recordType === 'todo' && 
        t.status !== 'done' &&
        t.module !== 'TEMPLATE_CENTER' && 
        t.module !== 'template'
      );
      setItems(pending);
      setError(null);
    }, (err) => {
        console.error("Tasks listener error:", err);
        setError(err);
    });
    return () => unsubscribe();
  }, [isDeptUser, userDeptCode, debugViewDepartmentId]);

  const handleDelete = (id: string, title: string) => {
    if (isDeptUser && !debugViewDepartmentId) {
        alert("權限不足：部門帳號無法刪除待辦事項。");
        return;
    }
    requestDelete({
      targetType: 'task',
      targetId: id,
      summary: `待辦：${title}`,
      onConfirm: async () => {
        try {
            await db.collection("legal_work_logs").doc(id).delete();
            showToast("已刪除 1 筆待辦事項");
        } catch(e) { console.error(e); showToast("刪除失敗", "error"); }
      }
    });
  };

  const handleToggleStatus = async (item: LegalWorkLog) => {
      const effectiveDeptCode = debugViewDepartmentId || userDeptCode;
      const isAllowed = canEdit || (isDeptUser && item.assignedDeptCodes?.includes(effectiveDeptCode));
      if (!isAllowed) return;
      try {
          await updateLegalWorkLog(item.id!, {
              status: 'done',
              finalStatus: '已完成'
          });
          showToast("已完成待辦");
      } catch (e) {
          console.error(e);
          showToast("更新失敗", "error");
      }
  };

  const handleExport = () => {
      let data = items;
      if (exportStart && exportEnd) {
          data = data.filter(i => {
              const d = i.dueDate || (i.createdAt?.toDate ? i.createdAt.toDate().toISOString() : "");
              return d >= exportStart && d <= exportEnd;
          });
      }
      exportToExcel("Tasks", data);
      setShowExportModal(false);
  };

  const getPriorityLabel = (p?: string) => p === "High" || p === "急件" ? "高" : p === "Medium" ? "中" : "低";

  const renderDepts = (codes?: string[]) => {
      if (!codes || codes.length === 0) return null;
      return (
          <div style={{fontSize: 11, color: '#a5b4fc', marginTop: 2}}>
              相關: {codes.map(c => DEPT_CODE_TO_NAME[c] || c).join(", ")}
          </div>
      );
  };

  const effectiveDeptCode = debugViewDepartmentId || userDeptCode;

  return (
    <div style={{width:'100%'}}>
      <DataToolbar canEdit={canEdit} onCreate={() => { setEditingItem(null); setIsModalOpen(true); }} onExport={() => setShowExportModal(true)} />
      {error && <div className="err" style={{ marginBottom: 16 }}>無法讀取資料: {error.message}</div>}
      
      {debugViewDepartmentId && (
          <div style={{padding:8, marginBottom:10, background:'rgba(59, 130, 246, 0.1)', border:'1px dashed #3b82f6', borderRadius:4, fontSize:12, color:'#60a5fa'}}>
              目前檢視視角：{debugViewDepartmentId} ({DEPT_CODE_TO_NAME[debugViewDepartmentId] || '未知'})
          </div>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {items.map(item => (
          <div key={item.id} className="tile" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input type="checkbox" checked={false} onChange={() => handleToggleStatus(item)} style={{ width: 18, height: 18, cursor: "pointer" }} title="標記為完成" />
              <div>
                <div style={{ fontWeight: "bold", color: "#fff" }}>{item.title}</div>
                <div className="p" style={{ marginTop: 4 }}>
                    {item.dueDate ? `到期: ${item.dueDate} | ` : ""}
                    模組: {item.moduleLabel || item.module}
                </div>
                {renderDepts(item.assignedDeptCodes)}
                {item.summary && <div className="p" style={{fontSize:12, color:'#ccc', marginTop:2}}>{item.summary}</div>}
                
                {/* Approval Status Badge */}
                {item.overallApprovalStatus && item.overallApprovalStatus !== 'not_started' && (
                    <div style={{marginTop:4}}>
                        <span className="badge" style={{fontSize:10, borderColor:'#8b5cf6', color:'#8b5cf6'}}>
                            簽核: {item.overallApprovalStatus === 'in_review' ? '進行中' : item.overallApprovalStatus === 'approved' ? '已核准' : '已駁回'}
                        </span>
                    </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="badge" style={{ color: item.priority === "High" || item.priority === "急件" ? "var(--danger)" : "var(--muted)", borderColor: "currentColor" }}>{getPriorityLabel(item.priority)}</div>
              <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  <button className="smallbtn" style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => setPreviewItem(item)}>預覽</button>
                  {(canEdit || (isDeptUser && item.assignedDeptCodes?.includes(effectiveDeptCode)) || debugViewDepartmentId) && (
                      <button className="smallbtn" style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => { setEditingItem(item); setIsModalOpen(true); }}>
                          {isDeptUser && !debugViewDepartmentId ? "更新" : "修改"}
                      </button>
                  )}
                  {canEdit && (
                      <button className="smallbtn" style={{ padding: "4px 8px", fontSize: 12, color: "var(--danger)", borderColor: "var(--danger)", background: "rgba(255,93,108,0.1)" }} onClick={() => handleDelete(item.id!, item.title)}>刪除</button>
                  )}
              </div>
            </div>
          </div>
        ))}
        {!error && items.length === 0 && <div className="p" style={{ textAlign: "center", padding: 20 }}>目前無待辦事項</div>}
      </div>

      {previewItem && <PreviewModal data={previewItem} onClose={() => setPreviewItem(null)} />}
      
      {isModalOpen && (
          <TaskForm 
            initialData={editingItem} 
            onClose={() => setIsModalOpen(false)} 
            onSuccess={() => { showToast(editingItem ? "已更新" : "已新增"); setIsModalOpen(false); }} 
            isDeptUser={isDeptUser && !debugViewDepartmentId} // If debug mode, treat as potentially admin view or mocked dept view
          />
      )}
      
      {showExportModal && (
          <Modal title="匯出待辦事項" onClose={() => setShowExportModal(false)} width={400}>
              <div className="grid">
                  <div className="p">請選擇匯出的日期區間：</div>
                  <DateField label="起始日" value={parseDate(exportStart)} onChange={d => setExportStart(formatDate(d))} />
                  <DateField label="結束日" value={parseDate(exportEnd)} onChange={d => setExportEnd(formatDate(d))} />
              </div>
              <div className="row" style={{marginTop:20, justifyContent:'flex-end'}}>
                  <button className="btn" onClick={handleExport}>匯出Excel</button>
              </div>
          </Modal>
      )}
    </div>
  );
}

export function TasksPage({ role }: any) {
    return <TasksView />;
}

function TaskForm({ initialData, onClose, onSuccess, isDeptUser }: { initialData: LegalWorkLog|null, onClose:()=>void, onSuccess:()=>void, isDeptUser: boolean }) {
  const { user } = useAuth();
  const [form, setForm] = React.useState<Partial<LegalWorkLog>>(initialData || { 
      priority: "Medium", status: "pending", attachments: [], recordType: "todo", module: "other",
      approvalSteps: INITIAL_APPROVAL_STEPS, overallApprovalStatus: "not_started", todoStatus: "open",
      plannedActions: "", expectedOutcome: "", plannedWorkDescription: ""
  });
  const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const handleChange = (k: any, v: any) => setForm(p => ({ ...p, [k]: v }));
  
  // Can current user act on approval?
  const isAssignee = form.userName?.includes(user?.displayName || "") || isGovAdmin(user?.email); // simplified

  const handleSave = async () => {
    if (!form.title) { alert("請填寫標題"); return; }
    setIsSubmitting(true);
    try {
        const payload = { ...form };
        const currentAttachments = payload.attachments || [];
        let docId = payload.id;

        if (initialData && docId) {
            await updateLegalWorkLog(docId, payload);
        } else {
            docId = await createLegalWorkLog({
                userId: user?.id || "",
                userName: user?.displayName || "User",
                module: form.module || "other",
                recordType: "todo",
                action: "PENDING",
                refId: "TASK-" + Date.now(),
                title: form.title,
                summary: form.summary,
                description: form.description || form.summary,
                status: "pending",
                priority: form.priority,
                dueDate: form.dueDate,
                finalStatus: "pending",
                counterpartOrDept: form.counterpartOrDept,
                attachments: currentAttachments,
                plannedActions: form.plannedActions,
                expectedOutcome: form.expectedOutcome,
                plannedWorkDescription: form.plannedWorkDescription, // New field
                aiSummaryForAssignee: form.aiSummaryForAssignee,
                // Pass approval fields
                extra: { 
                    approvalSteps: form.approvalSteps, 
                    overallApprovalStatus: form.overallApprovalStatus, 
                    todoStatus: form.todoStatus 
                }
            }) || undefined;
        }

        if (pendingFiles.length > 0 && docId) {
            const uploaded = await uploadTaskFiles(docId, pendingFiles);
            const finalAttachments = [...currentAttachments, ...uploaded];
            await updateLegalWorkLog(docId, { attachments: finalAttachments });
        }
        onSuccess();
    } catch(e) {
        console.error(e);
        alert("儲存失敗");
    } finally {
        setIsSubmitting(false);
    }
  };

  const removeFile = async (f: AttachmentMeta, idx: number) => {
      const next = [...(form.attachments || [])];
      next.splice(idx, 1);
      handleChange("attachments", next);
  };

  const handleApprovalUpdate = (newSteps: any[], newStatus: string) => {
      handleChange("approvalSteps", newSteps);
      handleChange("overallApprovalStatus", newStatus);
  };

  return (
    <Modal title={initialData ? (isDeptUser ? "更新待辦進度" : "編輯待辦") : "新增待辦"} onClose={onClose} width={700}>
      <div className="grid">
        <div className="field">
            <label className="label">標題</label>
            <input className="input" value={form.title||""} onChange={e => handleChange("title", e.target.value)} disabled={isDeptUser} style={isDeptUser?{opacity:0.6}:{}} />
        </div>
        <DateField label="到期日" value={parseDate(form.dueDate||null)} onChange={d => handleChange("dueDate", formatDate(d))} disabled={isDeptUser} />
        
        {/* Status Dropdown */}
        <div className="field">
            <label className="label">執行狀態</label>
            <select className="input" value={form.todoStatus || "open"} onChange={e => handleChange("todoStatus", e.target.value)}>
                <option value="open">待處理 (Open)</option>
                <option value="in_progress">進行中 (In Progress)</option>
                <option value="ready_for_review">送簽中 (Ready for Review)</option>
                <option value="closed">已結案 (Closed)</option>
            </select>
        </div>

        <div className="field"><label className="label">模組</label>
            <select className="input" value={form.module} onChange={e => handleChange("module", e.target.value)} disabled={isDeptUser}>
                <option value="other">其他</option>
                <option value="contract">合約</option>
                <option value="case">案件</option>
                <option value="govProject">政府專案</option>
                <option value="GOVERNANCE">法規/公司治理</option>
            </select>
        </div>
        <div className="field"><label className="label">優先度</label><select className="input" value={form.priority} onChange={e => handleChange("priority", e.target.value)} disabled={isDeptUser}><option value="High">高</option><option value="Medium">中</option><option value="Low">低</option></select></div>
        
        {/* AI Summary Display */}
        {(form.aiSummary || form.aiSummaryForAssignee) && (
            <div className="field" style={{gridColumn:"1/-1", background:'rgba(255,255,255,0.05)', padding:8, borderRadius:6}}>
                <label className="label" style={{color:'#a5b4fc'}}>AI 摘要 / 法規依據</label>
                <div style={{fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap'}}>{form.aiSummaryForAssignee || form.aiSummary}</div>
            </div>
        )}

        <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label">備註 / 執行說明</label>
            <textarea className="input" rows={2} value={form.summary||""} onChange={e => handleChange("summary", e.target.value)} />
        </div>

        {/* Planned Work Description (New) - Overrides or Complements plannedActions */}
        <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label" style={{color: '#fbbf24'}}>預定工作事項 / 執行計畫</label>
            <textarea 
                className="input" 
                rows={4} 
                value={form.plannedWorkDescription || form.plannedActions || ""} 
                onChange={e => {
                    handleChange("plannedWorkDescription", e.target.value);
                    // Also sync to legacy plannedActions for compatibility
                    handleChange("plannedActions", e.target.value);
                }} 
                placeholder="請輸入工作計畫..."
            />
        </div>

        {/* Expected Outcome - Editable */}
        <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label">預期成果</label>
            <textarea 
                className="input" 
                rows={2} 
                value={form.expectedOutcome||""} 
                onChange={e => handleChange("expectedOutcome", e.target.value)} 
                placeholder="例：完成公告與核備..."
            />
        </div>
      </div>
      
      {/* Approval Flow Section */}
      <div style={{marginTop:20, paddingTop:16, borderTop:'1px solid #333'}}>
          <SimpleApprovalSection 
              steps={form.approvalSteps || INITIAL_APPROVAL_STEPS}
              overallStatus={form.overallApprovalStatus || "not_started"}
              onUpdate={handleApprovalUpdate}
              canSubmit={isAssignee}
              onStatusChange={(s) => handleChange("todoStatus", s)}
          />
      </div>

      <FileUploadKit 
        label="上傳附件 (多檔支援)"
        existingFiles={form.attachments || []} 
        onFilesSelected={setPendingFiles} 
        onRemoveFile={removeFile}
        multiple={true}
      />
      <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}><button className="btn" style={{ width: "auto" }} disabled={isSubmitting} onClick={handleSave}>{isSubmitting ? "儲存中..." : "儲存"}</button></div>
    </Modal>
  );
}
